import {
    Box,
    Button,
    Container,
    createStyles,
    Divider,
    Grid,
    Theme,
    Typography,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import SwiperCore, { Mousewheel, Virtual } from "@theankur/swiper/core";
import { Swiper, SwiperSlide } from "@theankur/swiper/react";
import classNames from "classnames";
import { debounce } from "lodash";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Category } from "shared/src/models/category";
import { Id } from "shared/src/models/framework/db";
import { UserInfo } from "shared/src/models/user/user-info";
import { pollImpressionAdd, trackPoll, votePoll } from "shared/src/network/requests/poll/poll-req";
import { PollCard } from "shared/src/network/requests/poll/poll-req-models";
import * as workerTimers from "worker-timers";
import { Analytic } from "../analytic/analytic";
import Poll from "../components/Poll";
import { SocketContext } from "../context/socket";
import { withRouter, WithRouterProps } from "../hoc/withRouter";
import { OneSignal } from "../integrations/one-signal/one-signal";
import { setShowInterestDialog } from "../redux/app/AppSlice";
import { RootState } from "../redux/store";
import { commonClasses, spacing } from "../styles";
import { colors } from "../theme";
import { getMediaPath } from "../utils/media";
import { Timer, TimerType } from "../utils/timer";
import KButton from "./Button";
import PollContainer from "./PollContainer";
import Question from "./Question";
import User from "./User";

declare global {
    interface Window {
        fbq: any;
    }
}

SwiperCore.use([Mousewheel, Virtual]);

const styles = (theme: Theme) =>
    createStyles({
        ...commonClasses,
        ...spacing(theme),
        container: {
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
        },
        pollContainer: {
            backgroundColor: "#fbfbfd",
        },
        questionContainer: {
            width: `calc(100% + ${theme.spacing(4)})`,
            marginLeft: theme.spacing(-2),
            backgroundColor: "#f8f8f6",
        },
        option: {
            height: "160px",
        },
    });

type OwnProps = {
    polls: Array<Id<PollCard>>;
    uid?: string;
    className?: string;
    availableHeight?: number;
    user?: UserInfo;
    categories?: Id<Category>[];
    defaultActivePollId?: string;
    onLoadMorePolls?: (currentIndex: number) => void;
    onCommentSend?: (pollId: string, comment: string) => Promise<void>;
    onOptionSelected?: (poll: Id<PollCard>) => void;
    onInterestChange?: (selectedCategoriesId: string[]) => void;
    setShowUserGuide?: (showUserGuide: boolean) => void;
};

const mapState = (state: RootState) => ({
    activeBottomNavigation: state.app.activeBottomNavigation,
    commentDrawerVisibility: state.app.commentDrawerVisibility,
    showInterestDialog: state.app.showInterestDialog,
});

const mapDispatch = {
    setShowInterestDialog,
};

const connector = connect(mapState, mapDispatch);

type Props = WithStyles<typeof styles> &
    ConnectedProps<typeof connector> &
    OwnProps &
    WithRouterProps;

type State = {
    activePoll?: Id<PollCard>;
    pollTimerMap: Record<string, number>;
};

const appInstallOptionId = "app-download-poll-option-1";

const appDownloadPoll = {
    id: "app-download-poll",
    durationToInteract: 0,
    questionText: ["Back your team during the entire EPL? And win 2 free tickets to their match?"],
    creatorId: "",
    totalVote: 1473,
    totalComment: 0,
    startDate: new Date(),
    endDate: new Date(),
    meta: {
        created: new Date(),
        modified: new Date(),
    },
    options: [
        {
            id: appInstallOptionId,
            text: ["Yes! Download the FlashMob app for me to personalize and win."],
            totalVote: 1473,
        },
        {
            id: "app-download-poll-option-2",
            text: ["No, I don’t love Football. Bye!"],
            totalVote: 0,
        },
    ],
};

class Component extends React.Component<Props, State> {
    swiper?: SwiperCore;
    requestingPolls: { top: boolean; bottom: boolean };
    static contextType = SocketContext;
    context!: React.ContextType<typeof SocketContext>;
    workerTimerId?: number;
    optionSelectCount: number;
    isAlreadyAskOneSignal: boolean;
    prefetchTracker: string[];
    answerTimer?: TimerType;
    isAnsAutoSwipeStare: boolean;
    static isTabVisible = true;
    constructor(props: Props) {
        super(props);
        this.requestingPolls = { top: false, bottom: false };
        this.state = {
            pollTimerMap: {},
        };
        this.optionSelectCount = 0;
        this.isAlreadyAskOneSignal = false;
        this.prefetchTracker = [];
        this.isAnsAutoSwipeStare = false;
    }

    componentDidMount = () => {
        const { polls, defaultActivePollId } = this.props;
        if (polls.length) {
            this.setFirstPoll(defaultActivePollId);
        }
        // If swiper is initialised before component is mounted then update it
        this.swiper?.update();
        document.addEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        this.swiper?.on("touchStart", () => {
            this.answerTimer?.pause();
        });
        this.swiper?.on("touchEnd", () => {
            this.answerTimer?.resume();
        });
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const { polls, commentDrawerVisibility } = this.props;
        const {
            polls: prevPolls,
            commentDrawerVisibility: prevCommentDrawerVisibility,
        } = prevProps;
        const { activePoll: prevActivePoll } = prevState;
        const { activePoll } = this.state;
        if (prevCommentDrawerVisibility !== commentDrawerVisibility) {
            if (commentDrawerVisibility) {
                this.cancelAutoSwipe();
            } else {
                this.autoSwipeToNext();
            }
        }
        // Adjust current poll on swiper based on poll list update
        if (prevPolls.length === 0 && polls.length > 0) {
            this.swiper?.virtual.update(true);
            this.setFirstPoll();
        } else {
            const swiperIndex = polls.findIndex((each) => each.id === activePoll?.id);
            if (prevPolls.length !== polls.length) {
                this.swiper?.virtual.update(true);
                if (
                    this.swiper?.activeIndex !== undefined &&
                    polls.length > this.swiper?.activeIndex &&
                    swiperIndex === -1
                ) {
                    this.setActivePoll(this.swiper?.activeIndex);
                } else if (swiperIndex !== -1 && swiperIndex !== this.swiper?.activeIndex) {
                    this.swiper?.slideTo(swiperIndex, 0);
                }
            } else {
                if (
                    this.swiper?.activeIndex !== undefined &&
                    polls.length > this.swiper?.activeIndex &&
                    swiperIndex === -1
                ) {
                    this.setActivePoll(this.swiper?.activeIndex);
                }
            }
        }
        const isPollChange = prevActivePoll?.id !== activePoll?.id;

        if (isPollChange && activePoll) {
            Analytic.addEvent({
                event: "poll_impression",
                question: JSON.stringify(activePoll.questionText),
                pollType: activePoll?.pollType || "",
                defaultGTMConfig: "false",
            });
            if (!activePoll.pollSeenTime) pollImpressionAdd({ pollId: activePoll.id });
        }
        if (!!this.swiper && !!prevActivePoll && isPollChange) {
            Analytic.addEvent({
                event: "poll_swipe",
                swipeType: this.swiper.previousIndex > this.swiper.activeIndex ? "Up" : "Down",
                question: JSON.stringify(activePoll?.questionText),
                pollType: activePoll?.pollType || "",
                defaultGTMConfig: "false",
            });
        }
        if (!!this.swiper) {
            const index = this.swiper.activeIndex;
            if (index === Math.min(11, polls.length)) {
                const isOneSingleCall = !!localStorage.getItem("isOneSingleCall");
                if (!isOneSingleCall) {
                    this.isAlreadyAskOneSignal = true;
                    OneSignal.init(
                        "Always be heard. Never miss a FlashPoll. No spam - we promise."
                    );
                }
            }
        }
    };

    componentWillUnmount = () => {
        document.removeEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        if (this.workerTimerId) {
            workerTimers.clearInterval(this.workerTimerId);
            delete this.workerTimerId;
        }
    };

    handleDocumentVisibilityChange = () => {
        Component.isTabVisible = !document.hidden;
    };

    setFirstPoll = (activePollId?: string) => {
        const { polls } = this.props;
        let firstPollIndex: number;
        if (!!activePollId) {
            firstPollIndex = polls.findIndex((each) => each.id === activePollId);
        } else {
            firstPollIndex = polls.findIndex((each) => !each.pollSeenTime);
        }
        if (firstPollIndex === -1 && !!this.swiper) firstPollIndex = polls.length;
        if (firstPollIndex > -1) {
            this.swiper?.slideTo(firstPollIndex, 0);
            this.setActivePoll(firstPollIndex);
        }
    };

    handleSwiperInit = (swiper: SwiperCore) => {
        this.swiper = swiper;
        swiper.update();
        this.setFirstPoll();
    };

    handleOptionSelected = (pollId: string, optionId: string | null) => {
        const { polls, uid, onOptionSelected } = this.props;
        const { activePoll, pollTimerMap } = this.state;
        if (!uid) return;
        const poll =
            activePoll?.id === pollId ? activePoll : polls.find((each) => each.id === pollId);
        if (poll && !poll.myVote) {
            try {
                // Immediately slide to next poll and then submit request to backend
                const vote = { pollId: poll.id, userId: uid, optionId };
                // Put empty string as id,
                // because as we are not waiting for user vote api response to show instant result
                poll.myVote = { ...vote, id: "" };
                const option = poll.options.find((each) => each.id === optionId);
                if (option) option.totalVote = (option?.totalVote || 0) + 1;
                onOptionSelected?.(poll);
                this.optionSelectCount = this.optionSelectCount + 1;
                if (activePoll?.id === poll.id) {
                    this.autoSwipeToNext();
                }
                delete pollTimerMap[poll.id];
                this.setState({ pollTimerMap: { ...pollTimerMap } });
                // For instant result don't wait for poll vote response
                votePoll(vote);
            } catch {}
        }
    };

    handleSlideChange = async (swiper: SwiperCore) => {
        const { polls, onLoadMorePolls } = this.props;
        if (!!this.answerTimer) this.answerTimer.pause();
        this.answerTimer = undefined;
        this.setActivePoll(swiper.activeIndex);
        onLoadMorePolls?.(swiper.activeIndex);

        // Send fb event on poll 3 to track conversion
        if (swiper.activeIndex === 2 && window.fbq) {
            window.fbq("track", "ViewContent");
        }

        if (swiper.activeIndex === polls.length) {
            Analytic.addEvent({
                event: "poll_impression",
                question: JSON.stringify(appDownloadPoll.questionText),
                defaultGTMConfig: "false",
            });
        }

        // const index = this.optionSelectCount + swiper.activeIndex;
        // if (index === Math.min(10, polls.length) && !user?.categoryIds?.length) {
        //     this.setState({ showInterestDialog: true });
        // }
    };

    autoSwipeToNext = (sec?: number) => {
        if (!!this.answerTimer) this.answerTimer?.pause();
        this.answerTimer = undefined;
        this.isAnsAutoSwipeStare = false;
        const oldActivePoll = this.state.activePoll;
        this.answerTimer = Timer(() => {
            if (!!this.answerTimer) this.answerTimer?.pause();
            this.answerTimer = undefined;
            const { activePoll } = this.state;
            if (
                !!oldActivePoll &&
                !!activePoll &&
                oldActivePoll.id === activePoll.id &&
                !!activePoll?.myVote
            ) {
                Analytic.addEvent({
                    event: "poll_auto_swipe",
                    swipeType: "Down",
                    defaultGTMConfig: "true",
                });
                this.swiper?.slideNext();
            }
        }, (sec || 3) * 1000);
    };

    cancelAutoSwipe = () => {
        if (!!this.answerTimer) this.answerTimer.pause();
        this.answerTimer = undefined;
    };

    setActivePoll = async (index: number) => {
        const { polls } = this.props;
        this.managePollTimer(polls[index]);
        for (
            let pollIndex = Math.max(index - 2, 0);
            pollIndex <= Math.min(index + 2, polls.length - 1);
            pollIndex++
        ) {
            this.prefetchMedia(polls[pollIndex]);
        }
        this.setState(
            {
                activePoll: polls[index],
            },
            () => {
                const { activePoll } = this.state;
                if (!!activePoll?.myVote && !activePoll?.video) {
                    this.autoSwipeToNext();
                } else {
                    if (!!this.answerTimer) this.answerTimer.pause();
                    this.answerTimer = undefined;
                }
            }
        );
    };

    handleVideoEnded = (pollId: string) => {
        const { activePoll } = this.state;
        if (activePoll?.id === pollId && !!activePoll?.myVote) {
            this.autoSwipeToNext();
        }
    };

    prefetchHandler = (linkUrl: string) => {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = getMediaPath(linkUrl);
        document.head.appendChild(link);
    };

    prefetchMedia = (poll: Id<PollCard>) => {
        if (this.prefetchTracker.includes(poll.id)) return;
        if (poll.thumbnail) this.prefetchHandler(poll.thumbnail);
        if (poll.video) this.prefetchHandler(poll.video);
        poll.options.forEach((each) => {
            each.image?.forEach(this.prefetchHandler);
            each.video?.forEach(this.prefetchHandler);
            each.videoThumb?.forEach(this.prefetchHandler);
        });
        this.prefetchTracker.push(poll.id);
    };

    managePollTimer = (poll?: Id<PollCard>) => {
        const { pollTimerMap } = this.state;
        if (!poll || pollTimerMap[poll.id] || poll.myVote) return;
        pollTimerMap[poll.id] = poll.durationToInteract;
        this.setState({ pollTimerMap: { ...pollTimerMap } });
        if (!this.workerTimerId) {
            this.workerTimerId = workerTimers.setInterval(() => {
                const {
                    activeBottomNavigation,
                    commentDrawerVisibility,
                    showInterestDialog,
                } = this.props;
                const isFocused =
                    activeBottomNavigation === "polls" &&
                    !commentDrawerVisibility &&
                    !showInterestDialog;
                const { activePoll, pollTimerMap } = this.state;
                let stateChanged = false;
                // No ops condition
                if (!Component.isTabVisible || !isFocused) {
                    if (!!this.answerTimer) this.answerTimer.pause();
                    this.answerTimer = undefined;
                    this.isAnsAutoSwipeStare = true;
                    return;
                } else if (!!this.isAnsAutoSwipeStare && !!activePoll?.myVote) {
                    this.autoSwipeToNext();
                }
                const timeOutedPolls = this.props.polls.filter((each) => {
                    if (!pollTimerMap[each.id] || activePoll?.id !== each.id) return false;
                    stateChanged = true;
                    pollTimerMap[each.id] = pollTimerMap[each.id] - 1;
                    return pollTimerMap[each.id] <= 0;
                });
                timeOutedPolls.forEach((each) => {
                    delete pollTimerMap[each.id];
                    this.handleOptionSelected(each.id, null);
                });
                if (!Object.keys(pollTimerMap).length && this.workerTimerId) {
                    workerTimers.clearInterval(this.workerTimerId);
                    this.workerTimerId = undefined;
                }
                if (stateChanged) {
                    this.setState({ pollTimerMap: { ...pollTimerMap } });
                }
            }, 1000);
        }
    };

    handleTrackingChange = async (pollId: string, status: boolean) => {
        const { polls } = this.props;
        const poll = polls.find((each) => each.id === pollId);
        if (poll) {
            try {
                await trackPoll({ pollId: poll.id, status });
                poll.track = status;
                return Promise.resolve();
            } catch {
                return Promise.reject();
            }
        }
        return Promise.reject();
    };

    handleInterestSubmit = (selectedCategoriesId: string[]) => {
        const { setShowInterestDialog, onInterestChange } = this.props;
        setShowInterestDialog(false);
        onInterestChange?.(selectedCategoriesId);
        this.swiper?.slideTo(0, 0);
    };

    handleJoinWhatsAppClick = debounce(
        () => {
            window.open("https://chat.whatsapp.com/C8W1E7LmN9oLg8YUiqQl0A", "_blank");
        },
        300,
        { leading: true, trailing: false }
    );

    handleAppDownloadOptionSelected = debounce(
        (optionId: string) => {
            if (optionId === appInstallOptionId) {
                if (window.fbq) {
                    window.fbq("trackCustom", "AppInstallPollOptionClick");
                }
                window.open(
                    "https://play.google.com/store/apps/details?id=live.flashmob.app",
                    "_blank"
                );
            }
        },
        300,
        { leading: true, trailing: false }
    );

    renderWhatsAppSection = () => {
        const { polls, classes } = this.props;
        return (
            <SwiperSlide virtualIndex={polls.length}>
                <Container className={classes.container} maxWidth="sm">
                    <Grid container direction="column" alignItems="center">
                        <Typography sx={{ mb: 2 }} align="center">
                            More polls arriving soon...
                        </Typography>
                        <Typography align="center">
                            Meanwhile, join our WhatsApp community and never miss a flashpoll
                        </Typography>
                        <Grid
                            container
                            item
                            justifyContent="center"
                            sx={{ mt: 1 }}
                            data-event={"WhatsApp_feed"}
                            id="btn-join-whatsApp">
                            <KButton
                                color="whatsApp"
                                sx={{ backgroundColor: colors.thirdParty.whatsApp }}
                                onClick={this.handleJoinWhatsAppClick}
                                gaEventCategory="Create"
                                gaEventAction="User Create">
                                Join
                            </KButton>
                        </Grid>
                    </Grid>
                </Container>
            </SwiperSlide>
        );
    };

    renderAppLastSection = () => {
        const { polls, classes } = this.props;
        return (
            <SwiperSlide virtualIndex={polls.length}>
                <Container className={classes.container} maxWidth="sm">
                    <Grid
                        container
                        direction="column"
                        alignItems="center"
                        justifyContent="space-around"
                        sx={{ height: "100%", maxHeight: "40rem" }}
                        wrap="nowrap">
                        <Box sx={{ height: "5%" }} />
                        <img
                            style={{ width: "40px" }}
                            alt="tick circle"
                            src={
                                process.env.PUBLIC_URL +
                                "/images/broken-tick-09f8e8cac34095d5d55b5d07e805b100.webp"
                            }
                        />
                        <Typography sx={{ fontSize: "18px", fontWeight: "100" }}>
                            See You Soon With New Flashes.
                        </Typography>
                        <Box sx={{ height: "5%" }} />
                        <Divider sx={{ width: "100%" }} />
                        <Box sx={{ height: "10%" }} />
                        <Grid item sx={{ textAlign: "center" }}>
                            <Typography
                                sx={{
                                    fontSize: "18px",
                                    color: colors.brand.blue.main,
                                    fontWeight: "600",
                                }}>
                                Create Your Own Flash.
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    color: colors.brand.blue.main,
                                    fontWeight: "100",
                                }}>
                                Watch It Trend!
                            </Typography>
                        </Grid>
                        <Typography
                            sx={{
                                fontSize: "16px",
                                fontWeight: "100",
                                textAlign: "center",
                                maxWidth: "80%",
                            }}>
                            Flashmob gets your Flash
                            <br />
                            before 1000's of users.
                        </Typography>
                        <Box sx={{ height: "2%" }} />
                        {/* <img
                                style={{ width: "180px" }}
                                alt="Amazon Gift Voucher"
                                src={
                                    process.env.PUBLIC_URL +
                                    "/images/amazon-5000-bf35e9b31f48ca0835301752ea13d890.webp"
                                }
                            />
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: "100",
                                    textAlign: "center",
                                    maxWidth: "80%",
                                }}>
                                Win a ₹5000 Amazon gift card if the Flash created by you gets 1000+
                                votes. It's Easy!
                            </Typography> */}
                        <Button
                            sx={{ width: "150px" }}
                            variant="contained"
                            color="primary"
                            onClick={() => this.props.navigate("/create-flash")}>
                            Create Flash
                        </Button>
                        <Box sx={{ height: "10%" }} />
                    </Grid>
                </Container>
            </SwiperSlide>
        );
    };

    renderAppDownloadSection = () => {
        const { polls, classes, availableHeight } = this.props;
        return (
            <SwiperSlide virtualIndex={polls.length}>
                <Container className={classes.container} maxWidth="sm">
                    <PollContainer availableHeight={availableHeight}>
                        <Grid container direction="column" sx={{ height: "100%" }} wrap="nowrap">
                            <Grid item sx={{ zIndex: 1 }}>
                                <Grid container sx={{ mb: 1 }}>
                                    <Grid item xs={4}>
                                        <User
                                            creator={{ id: "flashmob" }}
                                            name="Flashmob"
                                            imageSrc={getMediaPath(
                                                "/user/flashmob-5d95fa4cd05952a72453ad1dbd6458c0.webp"
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={4}></Grid>
                                    <Grid item xs={4}>
                                        <Grid container direction="column" alignItems="center">
                                            <Grid item>
                                                <Typography variant="caption" noWrap>
                                                    Votes so far
                                                </Typography>
                                            </Grid>
                                            <Grid item className={classes.mt}>
                                                <Typography variant="subtitle2">1473</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs sx={{ position: "relative" }}>
                                <Question
                                    poll={appDownloadPoll}
                                    availableHeight={availableHeight}
                                    onOptionSelected={this.handleAppDownloadOptionSelected}
                                />
                                <Grid
                                    container
                                    direction="column"
                                    sx={{ position: "absolute", bottom: "10%" }}
                                    alignItems="center"
                                    justifyContent="center">
                                    <Grid item sx={{ mb: 2 }}>
                                        <Typography align="center" sx={{ fontSize: "15px" }}>
                                            FlashMob is created by footballers, for footballers.
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography align="center" sx={{ fontSize: "15px" }}>
                                            Free. Safe. No Ads. No tricks. No Profit.
                                        </Typography>
                                        <Typography
                                            align="center"
                                            sx={{ color: colors.red.original, fontSize: "15px" }}>
                                            ALL PASSION!
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    sx={{ position: "absolute", bottom: 0 }}
                                    alignItems="center"
                                    justifyContent="center">
                                    <Grid item>
                                        <Typography
                                            align="center"
                                            sx={{ color: "#666", fontSize: "11px" }}>
                                            @RGK Soft info services pvt. Ltd.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </PollContainer>
                </Container>
            </SwiperSlide>
        );
    };

    render = () => {
        const {
            classes,
            className,
            polls,
            uid,
            availableHeight,
            onCommentSend,
            // user,
            // categories,
            // activeBottomNavigation,
            // commentDrawerVisibility,
            // showInterestDialog,
        } = this.props;
        const { activePoll, pollTimerMap } = this.state;
        // const isFocused =
        //     activeBottomNavigation === "polls" && !commentDrawerVisibility && !showInterestDialog;
        return (
            <>
                {/* <InterestList
                    categories={categories}
                    open={showInterestDialog && !!categories?.length}
                    user={user || {}}
                    onSubmit={this.handleInterestSubmit}
                /> */}
                <Swiper
                    className={classNames(className)}
                    direction="vertical"
                    mousewheel
                    virtual
                    height={availableHeight}
                    onInit={this.handleSwiperInit}
                    onSlideChange={this.handleSlideChange}>
                    {polls.map((poll, index) => (
                        <SwiperSlide key={poll.id} virtualIndex={index}>
                            <Grid container className={classes.container}>
                                <Poll
                                    poll={poll}
                                    availableHeight={availableHeight}
                                    onOptionSelected={this.handleOptionSelected}
                                    onCommentSend={onCommentSend}
                                    onTrackingChange={this.handleTrackingChange}
                                    onVideoEnded={this.handleVideoEnded}
                                    isFocused={activePoll?.id === poll.id}
                                    remainingTime={pollTimerMap[poll.id]}
                                    uid={uid}
                                    showUserGuide={false}
                                    cancelCardAutoSwipe={this.cancelAutoSwipe}
                                />
                            </Grid>
                        </SwiperSlide>
                    ))}
                    {/* {this.renderWhatsAppSection()} */}
                    {/* {this.renderAppDownloadSection()} */}
                    {this.renderAppLastSection()}
                </Swiper>
                {/* {!!activePoll && <PollButton poll={activePoll} canPlayAudio={isFocused} />} */}
            </>
        );
    };
}

const BeforeWithRouter = withStyles(styles)(Component);
const PollSwiper = withRouter(BeforeWithRouter);

export default connector(PollSwiper);
